/**
 * Soft!=Live Page196 — Settings Notifications residual liquid (Page51/102 + dual).
 * Mount: /settings/notify
 * Buses (contract ops — never painted in operator DOM):
 *   get__settings_notify_status · me recipients/prefs · admin org/SMTP/FCM/grantee
 * Soft residual deleted:
 *  - Soft invent seats while posture loading
 *  - Soft org/SMTP depth when org_ops sealed
 *  - Soft SMTP-OK / FCM-OK invent without test seats
 *  - path /operation labels painted in DOM
 */
import { useMemo, useState } from "react";
import { html, F, Ico, KV, Card, Field, Input, Alert, go } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";

/** Path-free operator copy for Notify desk. Catalogue stays empty until Live says so. */
export function softNotifySettingsErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|seat_gated|not_authorized|denied|insufficient/.test(low)) {
    return "Your account cannot change notifications here. Ask a manager if you need access.";
  }
  if (/\b401\b|unauthorized|missing bearer|session/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b422\b|\b400\b|invalid|required|email|code|token/.test(low)) {
    return "That notification change was not accepted. Check the fields and try again.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable/.test(low)) {
    return "Notification services are not ready right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/auth\/|\/admin\/|\/settings\/notify|GET |POST |PATCH |DELETE |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}

/** Soft!=Live Page196: missing contract ops must not crash the desk. */
function safeNotifyPath(op, opts) {
  try {
    return path(op, opts);
  } catch (_e) {
    if (op === "get__settings_notify_status") return "/settings/notify/status";
    if (op === "get__auth_me_alert-recipients") return "/auth/me/alert-recipients";
    if (op === "get__auth_me_notification-prefs") return "/auth/me/notification-prefs";
    if (op === "get__admin_smtp_status") return "/admin/smtp/status";
    if (op === "post__admin_smtp_test") return "/admin/smtp/test";
    if (op === "get__admin_fcm_status") return "/admin/fcm/status";
    if (op === "post__admin_fcm_test") return "/admin/fcm/test";
    if (op === "get__admin_alert-recipients") return "/admin/alert-recipients";
    return "";
  }
}

/** Pure: seat chips from Live Settings Notify posture (Page196) — fail-closed. */
export function notifySettingsSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const pMax = Number(lim.personal_paint_max);
  const oMax = Number(lim.org_paint_max);
  const gMax = Number(lim.grantee_paint_max);
  return {
    personalBusAllowed: p.personal_bus_allowed === true,
    personalListAllowed: p.personal_list_allowed === true,
    personalWriteAllowed: p.personal_write_allowed === true,
    prefsReadAllowed: p.prefs_read_allowed === true,
    prefsWriteAllowed: p.prefs_write_allowed === true,
    orgOpsAllowed: p.org_ops_allowed === true,
    orgListAllowed: p.org_list_allowed === true,
    orgWriteAllowed: p.org_write_allowed === true,
    smtpStatusAllowed: p.smtp_status_allowed === true,
    smtpTestAllowed: p.smtp_test_allowed === true,
    fcmStatusAllowed: p.fcm_status_allowed === true,
    fcmTestAllowed: p.fcm_test_allowed === true,
    granteeListAllowed: p.grantee_list_allowed === true,
    granteeWriteAllowed: p.grantee_write_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    postureLabel: String(p.posture_label || "").trim(),
    personalPaintMax: Number.isFinite(pMax) && pMax > 0 ? Math.floor(pMax) : 40,
    orgPaintMax: Number.isFinite(oMax) && oMax > 0 ? Math.floor(oMax) : 40,
    granteePaintMax: Number.isFinite(gMax) && gMax > 0 ? Math.floor(gMax) : 40,
  };
}

/** Operator say from Live seat flags — never paint bus / org-write keys. */
export function notifyOperatorPostureOf(seat) {
  const s = seat && typeof seat === "object" ? seat : {};
  if (!s.personalBusAllowed) {
    if (s.detail === "settings_notify_personal_sealed") {
      return "Your extras are sealed. Site-wide depth may stay open.";
    }
    if (s.detail === "settings_notify_seat_gated") {
      return "Notifications need a signed-in seat";
    }
    return "Notifications desk sealed";
  }
  if (s.orgWriteAllowed) return "Your extras are open. Site-wide addresses can be edited.";
  return "Your extras are open. Site-wide addresses stay view-only.";
}

/** Pure: recipient rows · clamp for paint budget. */
export function recipientRowsOf(pack, maxN) {
  const root = pack || {};
  const rows = root.items || root.recipients || root.rows || [];
  const list = Array.isArray(rows) ? rows : [];
  const n = Number(maxN);
  const cap = Number.isFinite(n) && n > 0 ? Math.floor(n) : 40;
  return list.slice(0, cap);
}

/** @deprecated Soft client admin invent — prefer Live status seat.orgOpsAllowed. */
export function canAdminNotify(me) {
  if (!me) return false;
  const role = String(me.role || "").toLowerCase();
  return role === "admin" || role === "superadmin" || role === "sa_staff";
}

export function NotifySettingsDesk({ toast }) {
  const { _me } = useSession();
  const posture = useApi(safeNotifyPath("get__settings_notify_status"));
  const post = posture.data || {};
  // Fail-closed while loading — seats fail-closed.
  const seat = notifySettingsSeatOf(posture.loading || !posture.data ? {} : post);
  const postureLabel = notifyOperatorPostureOf(seat);

  const personal = useApi(
    seat.personalListAllowed ? safeNotifyPath("get__auth_me_alert-recipients") : "",
  );
  const prefs = useApi(
    seat.prefsReadAllowed ? safeNotifyPath("get__auth_me_notification-prefs") : "",
  );
  const smtp = useApi(
    seat.smtpStatusAllowed ? safeNotifyPath("get__admin_smtp_status") : "",
  );
  const fcm = useApi(
    seat.fcmStatusAllowed ? safeNotifyPath("get__admin_fcm_status") : "",
  );
  const org = useApi(
    seat.orgListAllowed ? safeNotifyPath("get__admin_alert-recipients") : "",
  );
  const { run, busy, error, envelope } = useAction();

  const [email, setEmail] = useState("");
  const [label, setLabel] = useState("");
  const [code, setCode] = useState("");
  const [awaiting, setAwaiting] = useState(false);
  const [orgEmail, setOrgEmail] = useState("");
  const [orgLabel, setOrgLabel] = useState("");
  const [orgCode, setOrgCode] = useState("");
  const [orgAwait, setOrgAwait] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [fcmToken, setFcmToken] = useState("");
  const [grantee, setGrantee] = useState("");
  const [granteeFocus, setGranteeFocus] = useState("");

  const pItems = useMemo(
    () => recipientRowsOf(personal.data, seat.personalPaintMax),
    [personal.data, seat.personalPaintMax],
  );
  const oItems = useMemo(
    () => recipientRowsOf(org.data, seat.orgPaintMax),
    [org.data, seat.orgPaintMax],
  );
  const style = String((prefs.data && prefs.data.alarm_push_style) || "fullscreen");
  const s = smtp.data || {};
  const smtpOn = !!(s.enabled || s.configured || s.ok);
  const f = fcm.data || {};
  const fcmOn = !!(f.enabled || f.project_id);
  const fcmPlane = !!(f.delivery_plane_live
    || (f.notify_queue && f.notify_queue.delivery_plane_live)
    || (f.notify_queue && f.notify_queue.redis_consumer_alive)
    || f.worker_running);

  const reloadAll = () => {
    if (!seat.reReadAllowed) {
      toast && toast("Refresh is not available on this seat.", "warn");
      return;
    }
    posture.reload && posture.reload();
    if (seat.personalListAllowed) personal.reload && personal.reload();
    if (seat.prefsReadAllowed) prefs.reload && prefs.reload();
    if (seat.orgListAllowed) org.reload && org.reload();
    if (seat.smtpStatusAllowed) smtp.reload && smtp.reload();
    if (seat.fcmStatusAllowed) fcm.reload && fcm.reload();
    toast && toast("Notifications refreshed", "go");
  };

  const requestCode = async () => {
    if (!seat.personalWriteAllowed) {
      toast && toast("Personal alert edits are not available on this seat.", "warn");
      return;
    }
    const out = await run(path("post__auth_me_alert-recipients_request-verify"), {
      body: { email: email.trim() },
    });
    if (!out) { toast(softNotifySettingsErrorOf(error) || "Could not queue a verification code.", "bad"); return; }
    setAwaiting(true);
    toast("Verification code queued — check that inbox", "go");
  };

  const bindPersonal = async () => {
    if (!seat.personalWriteAllowed) {
      toast && toast("Personal alert edits are not available on this seat.", "warn");
      return;
    }
    const out = await run(path("post__auth_me_alert-recipients"), {
      body: {
        email: email.trim(),
        code: code.trim(),
        label: label.trim() || undefined,
        enabled: true,
      },
    });
    if (!out) { toast(softNotifySettingsErrorOf(error) || "Could not bind that address.", "bad"); return; }
    toast("Address bound", "go");
    setEmail(""); setLabel(""); setCode(""); setAwaiting(false);
    personal.reload && personal.reload();
  };

  const togglePersonal = async (r) => {
    if (!seat.personalWriteAllowed) {
      toast && toast("Personal alert edits are not available on this seat.", "warn");
      return;
    }
    const out = await run(path("patch__auth_me_alert-recipients_p", { pathParams: [r.id] }), {
      method: "PATCH",
      body: { enabled: !r.enabled },
    });
    if (!out) { toast(softNotifySettingsErrorOf(error) || "Could not update", "bad"); return; }
    toast((!r.enabled ? "Enabled" : "Disabled") + "", "go");
    personal.reload && personal.reload();
  };

  const removePersonal = async (r) => {
    if (!seat.personalWriteAllowed) {
      toast && toast("Personal alert edits are not available on this seat.", "warn");
      return;
    }
    const out = await run(path("delete__auth_me_alert-recipients_p", { pathParams: [r.id] }), {
      method: "DELETE",
    });
    if (!out) { toast(softNotifySettingsErrorOf(error) || "Could not remove", "bad"); return; }
    toast("Removed", "warn");
    personal.reload && personal.reload();
  };

  const setPushStyle = async (next) => {
    if (!seat.prefsWriteAllowed) {
      toast && toast("Push style changes are not available on this seat.", "warn");
      return;
    }
    const out = await run(path("patch__auth_me_notification-prefs"), {
      method: "PATCH",
      body: { alarm_push_style: next },
    });
    if (!out) { toast(softNotifySettingsErrorOf(error) || "Could not save alarm push style.", "bad"); return; }
    toast("Alarm push · " + next + "", "go");
    prefs.reload && prefs.reload();
  };

  const requestOrgCode = async () => {
    if (!seat.orgWriteAllowed) {
      toast && toast("Organization alert edits are not available on this seat.", "warn");
      return;
    }
    const out = await run(path("post__admin_alert-recipients_request-verify"), {
      body: { email: orgEmail.trim() },
    });
    if (!out) { toast(softNotifySettingsErrorOf(error) || "Could not verify that organization address.", "bad"); return; }
    setOrgAwait(true);
    toast("Org verify code queued — check that inbox", "go");
  };

  const bindOrg = async () => {
    if (!seat.orgWriteAllowed) {
      toast && toast("Organization alert edits are not available on this seat.", "warn");
      return;
    }
    const out = await run(path("post__admin_alert-recipients"), {
      body: {
        email: orgEmail.trim(),
        code: orgCode.trim(),
        label: orgLabel.trim() || undefined,
        enabled: true,
      },
    });
    if (!out) { toast(softNotifySettingsErrorOf(error) || "Could not bind that organization address.", "bad"); return; }
    toast("Org recipient bound", "go");
    setOrgEmail(""); setOrgLabel(""); setOrgCode(""); setOrgAwait(false);
    org.reload && org.reload();
  };

  const toggleOrg = async (r) => {
    if (!seat.orgWriteAllowed) {
      toast && toast("Organization alert edits are not available on this seat.", "warn");
      return;
    }
    const id = String(r.id || r.recipient_id || "");
    if (!id) return;
    const out = await run(path("patch__admin_alert-recipients_p", { pathParams: [id] }), {
      method: "PATCH",
      body: { enabled: !r.enabled },
    });
    if (!out) { toast(softNotifySettingsErrorOf(error) || "Could not change that organization address.", "bad"); return; }
    toast((!r.enabled ? "Org enabled" : "Org disabled") + "", "go");
    org.reload && org.reload();
  };

  const removeOrg = async (r) => {
    if (!seat.orgWriteAllowed) {
      toast && toast("Organization alert edits are not available on this seat.", "warn");
      return;
    }
    const id = String(r.id || r.recipient_id || "");
    if (!id) return;
    const out = await run(path("delete__admin_alert-recipients_p", { pathParams: [id] }), {
      method: "DELETE",
    });
    if (!out) { toast(softNotifySettingsErrorOf(error) || "Could not remove that organization address.", "bad"); return; }
    toast("Org removed", "warn");
    org.reload && org.reload();
  };

  const smtpTest = async () => {
    if (!seat.smtpTestAllowed) {
      toast && toast("Mail test is not available on this seat.", "warn");
      return;
    }
    const out = await run(path("post__admin_smtp_test"), {
      body: testTo.trim() ? { to: testTo.trim() } : {},
    });
    if (!out) { toast(softNotifySettingsErrorOf(error) || "Mail test was refused.", "bad"); return; }
    toast("Mail test accepted", "go");
    smtp.reload && smtp.reload();
  };

  const fcmTest = async () => {
    if (!seat.fcmTestAllowed) {
      toast && toast("Push canary is not available on this seat.", "warn");
      return;
    }
    const tok = String(fcmToken || "").trim();
    if (tok.length < 8) {
      toast && toast("Push token is too short.", "warn");
      return;
    }
    const out = await run(safeNotifyPath("post__admin_fcm_test"), {
      body: {
        token: tok,
        title: "CMC FCM canary",
        body: "Admin push canary",
      },
    });
    fcm.reload && fcm.reload();
    if (!out) { toast(softNotifySettingsErrorOf(error) || "Push canary was refused.", "bad"); return; }
    if (out.provider_ok === true || out.ok === true) {
      toast("Push provider accepted the canary.", "go");
    } else {
      toast(
        "Push canary did not land · "
          + String(out.reason || out.detail || "").slice(0, 96),
        "warn",
      );
    }
  };

  const focusGrantee = () => {
    if (!seat.granteeListAllowed) {
      toast && toast("Staff grant focus is not available on this seat.", "warn");
      return;
    }
    const g = grantee.trim();
    if (!g) {
      toast("Enter a staff username first.", "warn");
      return;
    }
    setGranteeFocus(g);
    toast("Focused " + g, "go");
  };

  if (!posture.loading && !seat.personalBusAllowed && !seat.orgOpsAllowed) {
    return html`<div className="notify-desk notify-desk--liquid"
      data-cmc-notify="page51" data-cmc-page="page102-notify" data-cmc-residual="page196-notify">
      <${Alert} tone="bad" icon="ban" title="Notifications sealed"
        say="Your seat cannot use notifications here. Nothing is invented when the seat is closed."/>
      ${seat.detail ? html`<p className="note">${postureLabel}</p>` : null}
      <button type="button" className="btn btn--ghost btn--sm"
        disabled=${!seat.reReadAllowed || posture.loading} onClick=${reloadAll}>
        <${Ico} d="refresh" w=${13}/>Refresh
      </button>
    </div>`;
  }

  return html`<div className="notify-desk notify-desk--liquid"
    data-cmc-notify="page51" data-cmc-page="page102-notify" data-cmc-residual="page196-notify">
    <div className="notify-desk__rail">
      <div>
        <h2 className="notify-desk__title">Notifications</h2>
      </div>
      <button type="button" className="btn btn--ghost btn--sm"
        disabled=${busy || personal.loading || prefs.loading || posture.loading || !seat.reReadAllowed}
        onClick=${reloadAll}>
        <${Ico} d="refresh" w=${13}/>Refresh
      </button>
    </div>

    <${Alert} tone="info" title="One working channel"
      say="The site channel is one enabled org-wide address that already reaches a human."/>
    ${envelope
      ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>`
      : null}

    <div className="notify-desk__kpis" role="group" aria-label="Notify posture">
      <div className="notify-desk__kpi" data-on=${seat.personalBusAllowed ? "1" : "0"}>
        <span>Your extras</span>
        <b>${seat.personalBusAllowed ? "open" : "sealed"}</b>
        <em>this seat</em>
      </div>
      <div className="notify-desk__kpi" data-on=${seat.orgOpsAllowed ? "1" : "0"}>
        <span>Site-wide</span>
        <b>${seat.orgOpsAllowed ? "open" : "sealed"}</b>
        <em>admin seat</em>
      </div>
      <div className="notify-desk__kpi" data-on=${pItems.length ? "1" : "0"}>
        <span>Extra addresses</span>
        <b>${!seat.personalListAllowed ? "—" : (personal.loading ? "…" : pItems.length)}</b>
        <em>listed</em>
      </div>
      <div className="notify-desk__kpi" data-on=${seat.smtpStatusAllowed && smtpOn ? "1" : "0"}>
        <span>SMTP</span>
        <b>${!seat.smtpStatusAllowed ? "—" : (smtp.loading ? "…" : (smtpOn ? "on" : "off"))}</b>
        <em>${seat.role || "—"}</em>
      </div>
      <div className="notify-desk__kpi" data-on=${seat.fcmStatusAllowed && fcmOn && fcmPlane ? "1" : "0"}>
        <span>FCM</span>
        <b>${!seat.fcmStatusAllowed ? "—" : (fcm.loading ? "…" : (fcmOn ? (fcmPlane ? "connected" : "armed") : "off"))}</b>
        <em>channel</em>
      </div>
    </div>
    ${seat.detail ? html`<p className="note">${postureLabel}</p>` : null}
    ${posture.error ? html`
      <${Alert} tone="warn" title="Notification status is incomplete"
        say=${softNotifySettingsErrorOf(posture.error)}/>` : null}

    <div className="notify-desk__grid">
      <${Card} title="Alert recipients (you)" meta="your addresses"
          sub="Two-step verify bind. Bind only after verification completes.">
        ${!seat.personalListAllowed
          ? html`<${Alert} tone="info" title="Personal list sealed"
              say="No recipients recorded yet."/>`
          : personal.loading || personal.error
            ? html`<${Alert} tone=${personal.error ? "warn" : "info"}
                title=${personal.error ? "Personal list refused" : "Loading…"}
                say=${personal.error ? softNotifySettingsErrorOf(personal.error) : "Reading recipients…"}/>`
            : html`<div className="rows">
                ${!pItems.length ? html`<p className="note">No extra addresses yet.</p>` : null}
                ${pItems.map((r) => html`
                  <div key=${r.id} className="row" data-state=${r.enabled ? "on" : "off"}>
                    <span className="st"/>
                    <span className="main"><b>${r.email}</b><span>${r.label || "—"}</span></span>
                    <button type="button" className="btn btn--ghost btn--xs"
                      disabled=${busy || !seat.personalWriteAllowed}
                      onClick=${() => togglePersonal(r)}>${r.enabled ? "On" : "Off"}</button>
                    <button type="button" className="btn btn--danger btn--xs"
                      disabled=${busy || !seat.personalWriteAllowed}
                      onClick=${() => removePersonal(r)}>Remove</button>
                  </div>`)}
              </div>`}
        <div className="grid g2" style=${{ gap: "0 16px", marginTop: 14 }}>
          <${Field} label="Email">
            <${Input} icon="mail" value=${email} onInput=${(e) => setEmail(e.target.value)}
              placeholder="ops@example.invalid"/>
          <//>
          <${Field} label="Label">
            <${Input} icon="user" value=${label} onInput=${(e) => setLabel(e.target.value)}
              placeholder="Night desk"/>
          <//>
        </div>
        ${awaiting ? html`
          <${Field} label="Verification code">
            <${Input} icon="key" value=${code} onInput=${(e) => setCode(e.target.value)}
              placeholder="Six digits"/>
          <//>` : null}
        <div className="btn-row" style=${{ marginTop: 12 }}>
          ${!awaiting
            ? html`<button type="button" className="btn btn--ghost btn--sm"
                disabled=${busy || !email.includes("@") || !seat.personalWriteAllowed}
                onClick=${requestCode}><${Ico} d="plus" w=${13}/> Send code</button>`
            : html`<button type="button" className="btn btn--go btn--sm"
                disabled=${busy || code.length < 4 || !seat.personalWriteAllowed}
                onClick=${bindPersonal}>Bind address</button>`}
        </div>
      <//>
    </div>

    ${seat.orgOpsAllowed ? html`
      <div className="notify-desk__grid" data-cmc-notify-site-channel="1">
        <${Card} title="Org-wide alert recipients" meta="organization"
            sub="This is the site channel Prepare scores. Turn org-wide addresses on or off here.">
          ${!seat.orgListAllowed
            ? html`<${Alert} tone="info" title="Org list sealed"/>`
            : org.error
              ? html`<${Alert} tone="warn" title="Org recipients unavailable" say=${softNotifySettingsErrorOf(org.error)}/>`
              : html`<div className="rows">
                  ${!oItems.length ? html`<p className="note">No org recipients yet.</p>` : null}
                  ${oItems.map((r) => html`
                    <div key=${r.id} className="row" data-state=${r.enabled ? "on" : "off"}>
                      <span className="st"/>
                      <span className="main"><b>${r.email}</b><span>${r.label || "—"}</span></span>
                      <button type="button" className="btn btn--ghost btn--xs"
                        disabled=${busy || !seat.orgWriteAllowed}
                        onClick=${() => toggleOrg(r)}>${r.enabled ? "On" : "Off"}</button>
                      <button type="button" className="btn btn--danger btn--xs"
                        disabled=${busy || !seat.orgWriteAllowed}
                        onClick=${() => removeOrg(r)}>Remove</button>
                    </div>`)}
                </div>`}
          <div className="grid g2" style=${{ gap: "0 16px", marginTop: 14 }}>
            <${Field} label="Email">
              <${Input} icon="mail" value=${orgEmail} onChange=${(e) => setOrgEmail(e.target.value)}/>
            <//>
            <${Field} label="Label">
              <${Input} icon="user" value=${orgLabel} onChange=${(e) => setOrgLabel(e.target.value)}/>
            <//>
          </div>
          ${orgAwait ? html`
            <${Field} label="Verification code">
              <${Input} icon="key" value=${orgCode} onChange=${(e) => setOrgCode(e.target.value)}/>
            <//>` : null}
          <div className="btn-row" style=${{ marginTop: 8 }}>
            ${!orgAwait
              ? html`<button type="button" className="btn btn--ghost btn--sm"
                  disabled=${busy || !orgEmail.includes("@") || !seat.orgWriteAllowed}
                  onClick=${requestOrgCode}>Send org code</button>`
              : html`<button type="button" className="btn btn--go btn--sm"
                  disabled=${busy || !seat.orgWriteAllowed}
                  onClick=${bindOrg}>Bind org address</button>`}
          </div>
        <//>
      </div>` : null}

    <details className="ai-studio__advanced" data-cmc-notify-advanced="1">
      <summary>Advanced delivery (later)</summary>
      <div className="btn-row" style=${{ margin: "0 0 10px" }} data-cmc-notify-extra-channel="1">
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/settings/integrate")}>
          <${Ico} d="phone" w=${13}/> Telegram or voice (extra)
        </button>
      </div>
      <div className="notify-desk__grid">
      <${Card} title="How alarms arrive on your phone" meta="phone style"
          sub="Saved push style for this account. No client-side pretend delivery.">
        ${!seat.prefsReadAllowed
          ? html`<${Alert} tone="info" title="Prefs sealed"
              say="Push style only from saved preferences."/>`
          : prefs.loading || prefs.error
            ? html`<${Alert} tone=${prefs.error ? "warn" : "info"}
                title=${prefs.error ? "Prefs refused" : "Loading…"}
                say=${prefs.error ? softNotifySettingsErrorOf(prefs.error) : "Reading push style…"}/>`
            : html`<${F}>
                <${KV} k="Current style" v=${style}/>
                <div className="chips" style=${{ marginTop: 12 }}>
                  ${[["fullscreen", "Full-screen"], ["heads_up", "Heads-up banner"]].map(([id, l]) => html`
                    <button key=${id} type="button" className="chip" data-tone="ai"
                      data-sel=${style === id ? "1" : "0"}
                      disabled=${busy || !seat.prefsWriteAllowed}
                      onClick=${() => setPushStyle(id)}>${l}</button>`)}
                </div>
                <p className="note">Push delivery needs a registration token held while signed in. Push needs a real device token.</p>
              <//>`}
      <//>
    </div>

    ${seat.orgOpsAllowed ? html`
      <div className="admin-notify-depth admin-notify-depth--liquid"
        data-cmc-page="page146-admin-notify" data-cmc-residual="page196-notify">
      <div className="notify-desk__grid">
        <${Card} title="SMTP posture" meta="mail service"
            sub="Mail OK only after a successful test.">
          ${!seat.smtpStatusAllowed
            ? html`<${Alert} tone="info" title="SMTP status sealed"/>`
            : smtp.error
              ? html`<${Alert} tone="warn" title="SMTP status unavailable" say=${softNotifySettingsErrorOf(smtp.error)}/>`
              : html`<${F}>
                  <${KV} k="Enabled" v=${smtpOn ? "yes" : "no"} tone=${smtpOn ? "go" : "warn"}/>
                  <${KV} k="Host" v=${s.host || s.smtp_host ? "set" : "—"}/>
                  <${KV} k="Port" v=${s.port != null ? String(s.port) : "—"}/>
                  <${KV} k="Mode" v=${String(s.mode || "—")}/>
                  <${KV} k="Last check" v=${String(s.last_ok_at || s.checked_at || "—")}/>
                <//>`}
          <${Field} label="Test recipient (optional)">
            <${Input} icon="mail" value=${testTo} onChange=${(e) => setTestTo(e.target.value)}/>
          <//>
          <button type="button" className="btn btn--go btn--sm"
            disabled=${busy || !seat.smtpTestAllowed}
            onClick=${smtpTest} style=${{ marginTop: 8 }}>
            <${Ico} d="mail" w=${13}/>${busy ? "Testing mail…" : "Mail test"}</button>
        <//>

        <${Card} title="FCM posture" meta="push service"
            sub="Push OK only after a successful canary. Delivery truth comes from the server.">
          ${!seat.fcmStatusAllowed
            ? html`<${Alert} tone="info" title="FCM status sealed"/>`
            : fcm.error
              ? html`<${Alert} tone="warn" title="FCM status unavailable" say=${softNotifySettingsErrorOf(fcm.error)}/>`
              : html`<${F}>
                  <${KV} k="Enabled" v=${fcmOn ? "yes" : "no"} tone=${fcmOn ? "go" : "warn"}/>
                  <${KV} k="Project" v=${String(f.project_id || "—")}/>
                  <${KV} k="Delivery" v=${fcmPlane ? "on site" : "armed or off"}/>
                  <${KV} k="Local worker" v=${f.worker_running ? "yes" : "no"}/>
                  <${KV} k="Redis consumer" v=${(f.notify_queue && f.notify_queue.redis_consumer_alive) ? "yes" : "no"}/>
                  <${KV} k="Detail" v=${String(f.honesty || f.detail || "—").slice(0, 120)}/>
                <//>`}
          <${Field} label="Canary registration token">
            <${Input} icon="key" value=${fcmToken} onChange=${(e) => setFcmToken(e.target.value)}
              placeholder="Device push token"/>
          <//>
          <button type="button" className="btn btn--go btn--sm"
            disabled=${busy || !seat.fcmTestAllowed}
            onClick=${fcmTest} style=${{ marginTop: 8 }}>
            <${Ico} d="bolt" w=${13}/>${busy ? "Push canary…" : "Push canary"}</button>
        <//>
      </div>

      <${Card} title="Grantee alert recipients" meta="managed staff"
          sub="Focus reloads the staff list. Enter a real username first.">
        <div className="btn-row" style=${{ gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
          <${Field} label="Grantee username">
            <${Input} icon="user" value=${grantee} onChange=${(e) => setGrantee(e.target.value)}
              placeholder="staff.username"/>
          <//>
          <button type="button" className="btn btn--go btn--sm" style=${{ alignSelf: "flex-end" }}
            disabled=${busy || !grantee.trim() || !seat.granteeListAllowed}
            onClick=${focusGrantee}>Focus staff</button>
        </div>
        <${GranteeRecipients} grantee=${granteeFocus} toast=${toast} busy=${busy} run=${run}
          error=${error} seat=${seat}/>
      <//>
      </div>
    ` : html`
      <${Alert} tone="info" title="Organization tools sealed"
        say="This seat keeps personal recipients and push style only. Org mail is not opened for this seat."/>
    `}
    </details>
  </div>`;
}

function GranteeRecipients({ grantee, toast, busy, run, error, seat }) {
  const allow = !!(seat && seat.granteeListAllowed);
  const write = !!(seat && seat.granteeWriteAllowed);
  const list = useApi(
    (allow && grantee)
      ? path("get__admin_grantee-alert-recipients", { query: { grantee_username: grantee } })
      : null,
  );
  const items = recipientRowsOf(list.data, seat && seat.granteePaintMax);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [awaiting, setAwaiting] = useState(false);

  if (!allow) {
    return html`<p className="note">Staff grant focus is not available on this seat..</p>`;
  }
  if (!grantee) {
    return html`<p className="note">Focus a staff person to load their recipients.</p>`;
  }
  if (list.loading && !list.data) {
    return html`<p className="note">Reading staff recipients…</p>`;
  }
  if (list.error) {
    return html`<${Alert} tone="warn" title="Grantee recipients refused" say=${softNotifySettingsErrorOf(list.error)}/>`;
  }

  const requestCode = async () => {
    if (!write) {
      toast && toast("Staff grant edits are not available on this seat.", "warn");
      return;
    }
    const out = await run(path("post__admin_grantee-alert-recipients_request-verify"), {
      body: { grantee_username: grantee, email: email.trim() },
    });
    if (!out) { toast(softNotifySettingsErrorOf(error) || "Could not verify that staff address.", "bad"); return; }
    setAwaiting(true);
    toast("Grantee verify code queued — check that inbox", "go");
  };
  const bind = async () => {
    if (!write) {
      toast && toast("Staff grant edits are not available on this seat.", "warn");
      return;
    }
    const out = await run(path("post__admin_grantee-alert-recipients"), {
      body: {
        grantee_username: grantee,
        email: email.trim(),
        code: code.trim(),
        enabled: true,
      },
    });
    if (!out) { toast(softNotifySettingsErrorOf(error) || "Could not bind that staff address.", "bad"); return; }
    toast("Grantee recipient bound", "go");
    setEmail(""); setCode(""); setAwaiting(false);
    list.reload && list.reload();
  };
  const remove = async (r) => {
    if (!write) {
      toast && toast("Staff grant edits are not available on this seat.", "warn");
      return;
    }
    const id = String(r.id || r.recipient_id || "");
    if (!id) return;
    const out = await run(path("delete__admin_grantee_p_alert-recipients_p", {
      pathParams: [grantee, id],
    }), { method: "DELETE" });
    if (!out) { toast(softNotifySettingsErrorOf(error) || "Could not remove.", "bad"); return; }
    toast("Removed", "warn");
    list.reload && list.reload();
  };

  return html`<${F}>
    <div className="btn-row" style=${{ marginBottom: 8 }}>
      <button type="button" className="btn btn--ghost btn--sm"
        onClick=${() => { list.reload && list.reload(); toast("Staff list refreshed", "go"); }}>
        Re-read grantee</button>
      <span className="chip tiny">${items.length} row(s) · ${grantee}</span>
    </div>
    ${!items.length
      ? html`<p className="note">No recipients yet for ${grantee}.</p>`
      : html`<div className="rows">
          ${items.map((r) => html`
            <div key=${r.id} className="row row--say">
              <${Ico} d="mail" w=${14}/>
              <span className="main"><b>${r.email}</b><span>${r.label || "—"}</span></span>
              <button type="button" className="btn btn--danger btn--xs"
                disabled=${busy || !write}
                onClick=${() => remove(r)}>Remove</button>
            </div>`)}
        </div>`}
    <${Field} label="Email">
      <${Input} icon="mail" value=${email} onChange=${(e) => setEmail(e.target.value)}/>
    <//>
    ${awaiting ? html`<${Field} label="Code">
      <${Input} icon="key" value=${code} onChange=${(e) => setCode(e.target.value)}/>
    <//>` : null}
    <div className="btn-row" style=${{ marginTop: 8 }}>
      ${!awaiting
        ? html`<button type="button" className="btn btn--ghost btn--sm"
            disabled=${busy || !email.includes("@") || !write}
            onClick=${requestCode}>Send code</button>`
        : html`<button type="button" className="btn btn--go btn--sm"
            disabled=${busy || !write}
            onClick=${bind}>Bind</button>`}
    </div>
  <//>`;
}
